import { useDispatch, useSelector } from "react-redux";

import { ReceivableFilter } from "../../types/receivableFilter.types";
import { AppDispatch, RootState } from "../../../../store/store";
import ReceivableTableService from "../services/receivableTable.service";
import {
    clearPendingCustomerReceivables,
    clearReceivables,
    setError,
    setLoading,
    setPage,
    setPendingCustomerReceivables,
    setReceivables,
    setSize,
    setSort,
} from "../store/receivableTable.slice";

import useLoading from "../../../../hooks/useLoading";

import {
    resetReceivablePaymentForm,
    setPaymentSummary,
} from "../../payments/store/receivablePayment.slice";

export const useReceivableTable = () => {
    const dispatch: AppDispatch = useDispatch();

    const {
        showLoadingIndicator,
        hideLoadingIndicator,
    } = useLoading();

    const {
        list,
        paginator,
        pendingCustomerReceivables,
        page,
        size,
        sort,
        loading,
        error,
    } = useSelector(
        (state: RootState) => state.receivableTable
    );

    /**
     * 📊 Obtener cuentas por cobrar filtradas
     */
    const fetchReceivables = async (
        filters: ReceivableFilter,
        customPage?: number
    ) => {
        try {
            dispatch(setLoading(true));
            showLoadingIndicator();

            const currentPage = customPage ?? page;

            const data =
                await ReceivableTableService.getFiltered(
                    filters,
                    currentPage,
                    size,
                    sort
                );

            dispatch(setReceivables(data));
        } catch (err: any) {
            dispatch(
                setError("Error al cargar cuentas por cobrar")
            );
        } finally {
            dispatch(setLoading(false));
            hideLoadingIndicator();
        }
    };

    /**
     * 💰 Obtener resumen de pago por ID
     */
    const fetchPaymentSummary = async (
        receivableId: number
    ) => {
        try {
            showLoadingIndicator();

            const data =
                await ReceivableTableService.getPaymentSummary(
                    receivableId
                );

            if (data) {
                dispatch(
                    setPaymentSummary({
                        id: data.id,
                        customerId: data.customerId,
                        customer: data.customer,
                        number: data.number,
                        total: data.total,
                        paid: data.paid,
                        balance: data.balance,
                        status: data.status || "PENDING",
                    })
                );

                return data;
            }

            dispatch(resetReceivablePaymentForm());
            return null;
        } catch (err: any) {
            dispatch(
                setError("Error al obtener resumen de pago")
            );

            dispatch(resetReceivablePaymentForm());

            return null;
        } finally {
            hideLoadingIndicator();
        }
    };

    /**
 * 🔥 Obtener cuentas pendientes por cliente
 */
    const fetchPendingReceivablesByCustomer = async (
        customerId: number,
        search?: string
    ) => {
        try {
            showLoadingIndicator();

            /**
             * 🔥 obtener cuentas pendientes
             */
            const data =
                await ReceivableTableService.getPendingByCustomer(
                    customerId,
                    search
                );

            /**
             * 🔥 guardar en redux
             */
            dispatch(
                setPendingCustomerReceivables(
                    data || []
                )
            );
        } catch (err: any) {
            dispatch(
                setError(
                    "Error al obtener cuentas pendientes del cliente"
                )
            );

            dispatch(
                clearPendingCustomerReceivables()
            );
        } finally {
            hideLoadingIndicator();
        }
    };


    /**
 * 🔥 Obtener cuentas para historial de pagos por cliente
 */
    const fetchPaymentHistoryReceivablesByCustomer = async (
        customerId: number,
        search?: string
    ) => {
        try {
            showLoadingIndicator();

            const data =
                await ReceivableTableService.getPaymentHistoryByCustomer(
                    customerId,
                    search
                );

            dispatch(
                setPendingCustomerReceivables(
                    data || []
                )
            );
        } catch (err: any) {
            dispatch(
                setError(
                    "Error al obtener cuentas para historial de pagos"
                )
            );

            dispatch(
                clearPendingCustomerReceivables()
            );
        } finally {
            hideLoadingIndicator();
        }
    };

    /**
     * 📄 Cambiar página
     */
    const changePage = (
        newPage: number,
        filters: ReceivableFilter
    ) => {
        dispatch(setPage(newPage));
        fetchReceivables(filters, newPage);
    };

    /**
     * 📏 Cambiar tamaño de página
     */
    const changeSize = (
        newSize: number,
        filters: ReceivableFilter
    ) => {
        dispatch(setSize(newSize));
        fetchReceivables(filters, 0);
    };

    /**
     * 🔄 Cambiar orden
     */
    const changeSort = (
        newSort: string,
        filters: ReceivableFilter
    ) => {
        dispatch(setSort(newSort));
        fetchReceivables(filters, 0);
    };

    const clearFilteredReceivable = () => {
        dispatch(clearReceivables());
    };

    return {
        // state
        list,
        paginator,
        pendingCustomerReceivables,
        page,
        size,
        sort,
        loading,
        error,

        // actions
        fetchReceivables,
        fetchPaymentSummary,
        fetchPendingReceivablesByCustomer,
        fetchPaymentHistoryReceivablesByCustomer,
        changePage,
        changeSize,
        changeSort,
        clearFilteredReceivable,
    };
};